'use client';

// ⚠️ SENSITIVE FILE — Chart rendering & real-time data pipeline
// Changes here directly affect what users see on market charts (consensus, live prices, candles).
// If using AI assistance, MANUALLY TEST all chart variants before committing:
//   - Consensus mode (market share %)
//   - Live prices mode (line + candle)
//   - Pre-trade (new market, no data)
//   - During confirmation (resolving banner + pulsing line)
//   - Post-resolution (winner continues, loser clamped to 0)
//   - Mode switching (consensus ↔ live prices without "No data" flash)
//   - Timeframe switching (Live, 1H, 6H, 1D, 1W, 1M, All — windows larger than
//     the market's age are hidden; the lookback never shows empty data)
import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { ChartCandleIconSolid, TradeUpIcon } from '@/components/ui/icon/glyphs';
import { SegmentedControl, type SegmentedControlItem } from '@/components/ui/segmented-control';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import type { ChartInterval, ChartMode, RenderMode, YAxisProperty } from '@/lib/chart/types';
import { useChartSettingsStore } from '@/stores/chart-settings';
import { ChartDropdown } from './chart-dropdown';
import { LockedTimeframeSelector } from './locked-timeframe-selector';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Each entry is a lookback WINDOW (not a bucket); the backend bucket is derived
// in chart-factory.ts. `live` streams ticks; `all` spans the full lifespan.
const INTERVALS: readonly ChartInterval[] = ['live', '1h', '6h', '1d', '1w', '1mo', 'all'] as const;
type IntervalKey = 'interval_live' | 'interval_all' | 'interval_1h' | 'interval_6h' | 'interval_1d' | 'interval_1w' | 'interval_1mo';
const INTERVAL_KEYS: Record<ChartInterval, IntervalKey> = {
  live: 'interval_live',
  all: 'interval_all',
  '1h': 'interval_1h',
  '6h': 'interval_6h',
  '1d': 'interval_1d',
  '1w': 'interval_1w',
  '1mo': 'interval_1mo'
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ChartControlsProps {
  marketPubkey: string;
  outcomes?: ReadonlyArray<{
    index: number;
    title: string;
    color: string;
  }>;
  /** Set of intervals that have data. Intervals not in this set are hidden. */
  availableIntervals?: ReadonlySet<ChartInterval>;
  /**
   * Resolved market: the chart is frozen to its full creation→resolution
   * history, so the interval picker is replaced by a single non-interactive
   * "Locked" pill and the timeframe is fixed (the container forces `all`).
   */
  locked?: boolean;
  /**
   * Consensus vs live prices — same segmented control as the interval picker.
   * When omitted (rare embeds), the toggle is hidden.
   */
  onChartModeChange?: ((mode: ChartMode) => void) | undefined;
  chartMode?: ChartMode;
  interval?: ChartInterval;
  yAxisProperty?: YAxisProperty;
  onIntervalChange?: (interval: ChartInterval) => void;
  onYAxisPropertyChange?: (property: YAxisProperty) => void;
  /** Warms a live chart metric before it becomes the selected series. */
  onYAxisPropertyIntent?: (property: YAxisProperty) => void;
  /**
   * Winner/graduated chart: drop the consensus ("Market Share") entry so the
   * mode segment becomes a 2-way Market Cap / Price toggle. A graduated token
   * has no consensus view — it only ever renders in live mode, switching the
   * y-axis between market cap and price.
   */
  priceMarketCapOnly?: boolean;
  /** Renders left of the consensus/live segment (e.g. help + settings). */
  chartToolbarExtras?: ReactNode | undefined;
  /** Optional local render-mode ownership for diagnostics and embedded charts. */
  renderMode?: RenderMode;
  onRenderModeChange?: (mode: RenderMode) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Chart footer controls: timeframe interval picker; in live mode only — a
 * line/candle toggle; on the right — optional trailing actions (help, settings),
 * then the 3-way mode segment (`onChartModeChange`): Market Share (consensus) /
 * MCap / Price, the latter two being live mode with the matching y-axis.
 *
 * The line/candle toggle is hidden in consensus mode since it only applies to
 * the live chart. It IS shown in both Price and Market Cap modes — the backend
 * only serves OHLCV for price, so market-cap candles are derived from the price
 * OHLCV (see `connected-chart-container.tsx`'s `v2ChartData` builder).
 */
export function ChartControls(t0) {
  const $ = _c(82);
  const {
    availableIntervals,
    locked: t1,
    onChartModeChange,
    chartMode: chartModeProp,
    interval: intervalProp,
    yAxisProperty: yAxisPropertyProp,
    onIntervalChange,
    onYAxisPropertyChange,
    onYAxisPropertyIntent,
    priceMarketCapOnly: t2,
    chartToolbarExtras,
    renderMode: renderModeProp,
    onRenderModeChange
  } = t0;
  const locked = t1 === undefined ? false : t1;
  const priceMarketCapOnly = t2 === undefined ? false : t2;
  const t = useTranslations("chart");
  let t3;
  if ($[0] !== chartModeProp) {
    t3 = s => chartModeProp ?? s.chartMode;
    $[0] = chartModeProp;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const chartMode = useChartSettingsStore(t3);
  let t4;
  if ($[2] !== intervalProp) {
    t4 = s_0 => intervalProp ?? s_0.interval;
    $[2] = intervalProp;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const interval = useChartSettingsStore(t4);
  let t5;
  if ($[4] !== renderModeProp) {
    t5 = s_1 => renderModeProp ?? s_1.renderMode;
    $[4] = renderModeProp;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const renderMode = useChartSettingsStore(t5);
  let t6;
  if ($[6] !== yAxisPropertyProp) {
    t6 = s_2 => yAxisPropertyProp ?? s_2.yAxisProperty;
    $[6] = yAxisPropertyProp;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const yAxisProperty = useChartSettingsStore(t6);
  const setIntervalStore = useChartSettingsStore(_temp);
  const setRenderModeStore = useChartSettingsStore(_temp2);
  const setYAxisPropertyStore = useChartSettingsStore(_temp3);
  const setInterval = onIntervalChange ?? setIntervalStore;
  const setRenderMode = onRenderModeChange ?? setRenderModeStore;
  const setYAxisProperty = onYAxisPropertyChange ?? setYAxisPropertyStore;
  const isLive = chartMode === "live";
  const isMobile = useMediaQuery("(max-width: 767px)");
  let t7;
  if ($[8] !== onYAxisPropertyIntent || $[9] !== setYAxisProperty) {
    t7 = function handleYAxisChange(property) {
      onYAxisPropertyIntent?.(property);
      setYAxisProperty(property);
    };
    $[8] = onYAxisPropertyIntent;
    $[9] = setYAxisProperty;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleYAxisChange = t7;
  const chartModeValue = chartMode === "consensus" ? "consensus" : yAxisProperty;
  let t8;
  if ($[11] !== handleYAxisChange || $[12] !== onChartModeChange) {
    t8 = function handleChartModeSelect(value) {
      selectChartMode(value, onChartModeChange, handleYAxisChange);
    };
    $[11] = handleYAxisChange;
    $[12] = onChartModeChange;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleChartModeSelect = t8;
  let t9;
  if ($[14] !== availableIntervals || $[15] !== t) {
    t9 = INTERVALS.map(iv => ({
      key: iv,
      label: t(INTERVAL_KEYS[iv]),
      hidden: availableIntervals !== undefined && !availableIntervals.has(iv)
    }));
    $[14] = availableIntervals;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const intervalItems = t9;
  let t10;
  if ($[17] !== t) {
    t10 = t("render_line");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t("render_line");
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11) {
    t12 = {
      content: <p className="text-caption">{t11}</p>,
      side: "bottom"
    };
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12) {
    t13 = {
      key: "line",
      icon: TradeUpIcon,
      ariaLabel: t10,
      tooltip: t12
    };
    $[23] = t10;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t) {
    t14 = t("render_candle");
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t) {
    t15 = t("render_candle");
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15) {
    t16 = {
      content: <p className="text-caption">{t15}</p>,
      side: "bottom"
    };
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t14 || $[33] !== t16) {
    t17 = {
      key: "candle",
      icon: ChartCandleIconSolid,
      iconClassName: "size-3",
      ariaLabel: t14,
      tooltip: t16
    };
    $[32] = t14;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t13 || $[36] !== t17) {
    t18 = [t13, t17];
    $[35] = t13;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const renderModeItems = t18;
  let t19;
  if ($[38] !== priceMarketCapOnly || $[39] !== t) {
    t19 = priceMarketCapOnly ? [] : [{
      key: "consensus",
      label: t("tabs.consensus"),
      tooltip: {
        content: <p className="max-w-52 text-caption">{t("tabs_consensus_tooltip")}</p>,
        side: "top" as const
      }
    }];
    $[38] = priceMarketCapOnly;
    $[39] = t;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t) {
    t20 = t("y_axis_market_cap");
    $[41] = t;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t) {
    t21 = t("tabs_live_tooltip");
    $[43] = t;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t21) {
    t22 = {
      content: <p className="max-w-52 text-caption">{t21}</p>,
      side: "top"
    };
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t20 || $[48] !== t22) {
    t23 = {
      key: "marketCap",
      label: t20,
      tooltip: t22
    };
    $[47] = t20;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t) {
    t24 = t("y_axis_price");
    $[50] = t;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t) {
    t25 = t("tabs_live_tooltip");
    $[52] = t;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t25) {
    t26 = {
      content: <p className="max-w-52 text-caption">{t25}</p>,
      side: "top"
    };
    $[54] = t25;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t24 || $[57] !== t26) {
    t27 = {
      key: "price",
      label: t24,
      tooltip: t26
    };
    $[56] = t24;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t19 || $[60] !== t23 || $[61] !== t27) {
    t28 = [...t19, t23, t27];
    $[59] = t19;
    $[60] = t23;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  const chartModeItems = t28;
  const t29 = onChartModeChange !== undefined;
  const t30 = isLive && !locked;
  let t31;
  if ($[63] !== chartModeItems || $[64] !== chartModeValue || $[65] !== chartToolbarExtras || $[66] !== handleChartModeSelect || $[67] !== interval || $[68] !== intervalItems || $[69] !== locked || $[70] !== onYAxisPropertyIntent || $[71] !== renderMode || $[72] !== renderModeItems || $[73] !== setInterval || $[74] !== setRenderMode || $[75] !== t29 || $[76] !== t30) {
    t31 = {
      locked,
      intervalItems,
      interval,
      setInterval,
      chartModeItems,
      chartModeValue,
      showChartMode: t29,
      handleChartModeSelect,
      onYAxisPropertyIntent,
      showRenderMode: t30,
      renderModeItems,
      renderMode,
      setRenderMode,
      chartToolbarExtras
    };
    $[63] = chartModeItems;
    $[64] = chartModeValue;
    $[65] = chartToolbarExtras;
    $[66] = handleChartModeSelect;
    $[67] = interval;
    $[68] = intervalItems;
    $[69] = locked;
    $[70] = onYAxisPropertyIntent;
    $[71] = renderMode;
    $[72] = renderModeItems;
    $[73] = setInterval;
    $[74] = setRenderMode;
    $[75] = t29;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  const viewProps = t31;
  if (isMobile) {
    let t32;
    if ($[78] !== viewProps) {
      t32 = <MobileChartControls {...viewProps} />;
      $[78] = viewProps;
      $[79] = t32;
    } else {
      t32 = $[79];
    }
    return t32;
  }
  let t32;
  if ($[80] !== viewProps) {
    t32 = <DesktopChartControls {...viewProps} />;
    $[80] = viewProps;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  return t32;
}
function _temp3(s_5) {
  return s_5.setYAxisProperty;
}
function _temp2(s_4) {
  return s_4.setRenderMode;
}
function _temp(s_3) {
  return s_3.setInterval;
}
interface ChartControlsViewProps {
  readonly locked: boolean;
  readonly intervalItems: SegmentedControlItem[];
  readonly interval: ChartInterval;
  readonly setInterval: (interval: ChartInterval) => void;
  readonly chartModeItems: SegmentedControlItem[];
  readonly chartModeValue: string;
  readonly showChartMode: boolean;
  readonly handleChartModeSelect: (value: string) => void;
  readonly onYAxisPropertyIntent: ((property: YAxisProperty) => void) | undefined;
  readonly showRenderMode: boolean;
  readonly renderModeItems: SegmentedControlItem[];
  readonly renderMode: 'line' | 'candle';
  readonly setRenderMode: (mode: 'line' | 'candle') => void;
  readonly chartToolbarExtras: ReactNode | undefined;
}
function MobileChartControls(t0) {
  const $ = _c(30);
  const {
    locked,
    intervalItems,
    interval,
    setInterval,
    chartModeItems,
    chartModeValue,
    showChartMode,
    handleChartModeSelect,
    showRenderMode,
    renderModeItems,
    renderMode,
    setRenderMode,
    chartToolbarExtras
  } = t0;
  let t1;
  if ($[0] !== intervalItems) {
    t1 = intervalItems.filter(_temp4).map(_temp5);
    $[0] = intervalItems;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timeframeItems = t1;
  let t2;
  if ($[2] !== chartModeItems) {
    t2 = chartModeItems.map(_temp6);
    $[2] = chartModeItems;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const chartModeDropdownItems = t2;
  let t3;
  if ($[4] !== interval || $[5] !== locked || $[6] !== setInterval || $[7] !== timeframeItems) {
    t3 = locked ? <LockedTimeframeSelector /> : <ChartDropdown items={timeframeItems} active={interval} onChange={key => setInterval(key as ChartInterval)} />;
    $[4] = interval;
    $[5] = locked;
    $[6] = setInterval;
    $[7] = timeframeItems;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== chartModeDropdownItems || $[10] !== chartModeValue || $[11] !== handleChartModeSelect || $[12] !== showChartMode) {
    t4 = showChartMode && <ChartDropdown items={chartModeDropdownItems} active={chartModeValue} onChange={handleChartModeSelect} />;
    $[9] = chartModeDropdownItems;
    $[10] = chartModeValue;
    $[11] = handleChartModeSelect;
    $[12] = showChartMode;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== setRenderMode) {
    t5 = key_0 => setRenderMode(key_0 as 'line' | 'candle');
    $[14] = setRenderMode;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== renderMode || $[17] !== renderModeItems || $[18] !== showRenderMode || $[19] !== t5) {
    t6 = <RenderModeToggle show={showRenderMode} items={renderModeItems} active={renderMode} onChange={t5} />;
    $[16] = renderMode;
    $[17] = renderModeItems;
    $[18] = showRenderMode;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t3 || $[22] !== t4 || $[23] !== t6) {
    t7 = <div className="flex min-w-0 items-center gap-2">{t3}{t4}{t6}</div>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== chartToolbarExtras) {
    t8 = <ChartToolbarExtras>{chartToolbarExtras}</ChartToolbarExtras>;
    $[25] = chartToolbarExtras;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <div className="flex items-center justify-between gap-2">{t7}{t8}</div>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
function _temp6(item_1) {
  return {
    key: item_1.key,
    label: item_1.label
  };
}
function _temp5(item_0) {
  return {
    key: item_0.key,
    label: item_0.label
  };
}
function _temp4(item) {
  return item.hidden !== true;
}
function DesktopChartControls(t0) {
  const $ = _c(27);
  const {
    locked,
    intervalItems,
    interval,
    setInterval,
    chartModeItems,
    chartModeValue,
    showChartMode,
    handleChartModeSelect,
    onYAxisPropertyIntent,
    showRenderMode,
    renderModeItems,
    renderMode,
    setRenderMode,
    chartToolbarExtras
  } = t0;
  let t1;
  if ($[0] !== interval || $[1] !== intervalItems || $[2] !== locked || $[3] !== setInterval) {
    t1 = locked ? <LockedTimeframeSelector /> : <SegmentedControl items={intervalItems} active={interval} onChange={key => setInterval(key as ChartInterval)} layoutId="chart-controls-interval-pill" />;
    $[0] = interval;
    $[1] = intervalItems;
    $[2] = locked;
    $[3] = setInterval;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== chartModeItems || $[6] !== chartModeValue || $[7] !== handleChartModeSelect || $[8] !== onYAxisPropertyIntent || $[9] !== showChartMode) {
    t2 = showChartMode && <SegmentedControl items={chartModeItems} active={chartModeValue} onChange={handleChartModeSelect} {...onYAxisPropertyIntent === undefined ? {} : {
      onItemPointerEnter: key_0 => selectYAxisPropertyIntent(key_0, onYAxisPropertyIntent)
    }} layoutId="chart-controls-mode-pill" />;
    $[5] = chartModeItems;
    $[6] = chartModeValue;
    $[7] = handleChartModeSelect;
    $[8] = onYAxisPropertyIntent;
    $[9] = showChartMode;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== setRenderMode) {
    t3 = key_1 => setRenderMode(key_1 as 'line' | 'candle');
    $[11] = setRenderMode;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== renderMode || $[14] !== renderModeItems || $[15] !== showRenderMode || $[16] !== t3) {
    t4 = <RenderModeToggle show={showRenderMode} items={renderModeItems} active={renderMode} onChange={t3} />;
    $[13] = renderMode;
    $[14] = renderModeItems;
    $[15] = showRenderMode;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t4) {
    t5 = <div className="flex min-w-0 flex-wrap items-center gap-2">{t1}{t2}{t4}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== chartToolbarExtras) {
    t6 = <ChartToolbarExtras>{chartToolbarExtras}</ChartToolbarExtras>;
    $[22] = chartToolbarExtras;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <div className="flex flex-wrap items-center justify-between gap-2">{t5}{t6}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
function ChartToolbarExtras(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  if (children === undefined) {
    return null;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <div className="inline-flex shrink-0 items-center gap-1">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function selectChartMode(value: string, onChartModeChange: ((mode: ChartMode) => void) | undefined, onYAxisPropertyChange: (property: YAxisProperty) => void): void {
  if (onChartModeChange === undefined) return;
  if (value === 'consensus') {
    onChartModeChange('consensus');
    return;
  }
  onChartModeChange('live');
  onYAxisPropertyChange(value as YAxisProperty);
}
function selectYAxisPropertyIntent(key: string, onYAxisPropertyIntent: (property: YAxisProperty) => void): void {
  if (key !== 'consensus') onYAxisPropertyIntent(key as YAxisProperty);
}

// ---------------------------------------------------------------------------
// Render-mode (line / candle) toggle
// ---------------------------------------------------------------------------

const RENDER_TOGGLE_TRANSITION = {
  duration: 0.2,
  ease: 'easeOut'
} as const;
interface RenderModeToggleProps {
  /** Live mode (Price or Market Cap) — the line/candle toggle is hidden in
   *  consensus mode and on resolved/locked charts. */
  show: boolean;
  items: SegmentedControlItem[];
  active: string;
  onChange: (key: string) => void;
}

/**
 * The line/candle sub-toggle, wrapped in an enter/exit animation so it fades +
 * scales in when the live Price chart is selected instead of popping the row.
 */
function RenderModeToggle(t0) {
  const $ = _c(8);
  const {
    show,
    items,
    active,
    onChange
  } = t0;
  const reduce = useReducedMotion();
  let t1;
  if ($[0] !== active || $[1] !== items || $[2] !== onChange || $[3] !== reduce || $[4] !== show) {
    t1 = show && <m.div key="render-toggle" className="w-fit" initial={reduce ? false : {
      opacity: 0,
      scale: 0.85
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.85
    }} transition={reduce ? {
      duration: 0
    } : RENDER_TOGGLE_TRANSITION}><SegmentedControl variant="chart-icon" items={items} active={active} onChange={onChange} layoutId="chart-controls-render-pill" /></m.div>;
    $[0] = active;
    $[1] = items;
    $[2] = onChange;
    $[3] = reduce;
    $[4] = show;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <LazyMotion features={domMax}><AnimatePresence initial={false}>{t1}</AnimatePresence></LazyMotion>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}